/**
 * DashboardLayout.tsx
 *
 * Root layout for all authenticated dashboard pages.
 *
 * Mobile behaviour (< lg / 1024px):
 *   - Sidebar is hidden off-screen by default.
 *   - Tapping the hamburger slides the sidebar in as a full-height overlay.
 *   - A semi-transparent backdrop covers the content; tapping it closes the sidebar.
 *   - Main content always uses ml-0 on mobile (no sidebar push).
 *
 * Desktop behaviour (≥ lg):
 *   - Sidebar is always visible, either full-width (256px) or icon-only (64px).
 *   - Main content is pushed right by ml-64 / ml-16 accordingly.
 *   - The hamburger button toggles the collapsed state.
 *
 * Structure:
 *   <Sidebar>                — collapsible left navigation
 *   <Topbar>                 — fixed top bar with sidebar toggle
 *   <AnnouncementBanner>     — optional company-wide banner
 *   <main>                   — scrollable content area
 *   <CommandPalette>         — Ctrl+K command palette overlay
 *   <HelpWidget>             — floating ? help button (bottom-right)
 *   <OnboardingTour>         — Shepherd.js first-run tour
 *   <KeyboardShortcutsModal> — keyboard cheatsheet
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import CommandPalette from '../command/CommandPalette';
import PulseBotPanel from '../pulsebot/PulseBotPanel';
import HelpWidget from '../help/HelpWidget';
import OnboardingTour from '../help/OnboardingTour';
import KeyboardShortcutsModal from '../help/KeyboardShortcutsModal';
import ToastContainer from '../notifications/ToastContainer';
import {
  applyPrintTitle, restorePrintTitle, printedPageTitle,
} from '../../lib/printDocumentTitle';
import { useCommandPalette } from '../../context/CommandPaletteContext';
import { usePulseBot } from '../../context/PulseBotContext';
import { AnnouncementBanner } from '../../pages/owner/BroadcastPage';
import TrialBanner from './TrialBanner';
import { routeSuppliesOwnRail } from '../../lib/ownRailRoutes';
import CookieConsentBanner from '../gdpr/CookieConsentBanner';
import ErrorBoundary from '../ErrorBoundary';

/** Breakpoint at which desktop sidebar behaviour kicks in (matches Tailwind `lg`). */
const LG_BREAKPOINT = 1024;

export default function DashboardLayout() {
  /**
   * collapsed — desktop state: sidebar icon-only (64px) vs full (256px).
   * mobileOpen — mobile state: sidebar overlay visible or hidden.
   */
  const [collapsed,   setCollapsed]   = useState(false);
  const [mobileOpen,  setMobileOpen]  = useState(false);
  const [isMobile,    setIsMobile]    = useState(() => window.innerWidth < LG_BREAKPOINT);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [tourRun, setTourRun] = useState(false);

  const { toggle: togglePalette } = useCommandPalette();
  const { toggle: togglePulseBot } = usePulseBot();

  /** Track viewport width to switch between mobile and desktop modes. */
  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < LG_BREAKPOINT;
      setIsMobile(mobile);
      // Auto-close the mobile overlay when resizing to desktop
      if (!mobile) setMobileOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /** Start onboarding tour automatically on first visit. */
  useEffect(() => {
    const seen = localStorage.getItem('pulsehq_tour_seen');
    if (!seen) {
      const t = setTimeout(() => setTourRun(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  /** Global Ctrl+K / Cmd+K listener for command palette. */
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault();
      togglePalette();
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'j') {
      e.preventDefault();
      togglePulseBot();
    }
    if (
      e.key === '?' &&
      !(e.target instanceof HTMLInputElement) &&
      !(e.target instanceof HTMLTextAreaElement)
    ) {
      setShortcutsOpen(true);
    }
    // togglePalette comes from a stable context — including it would
    // force handleKeyDown to be recreated on every render even though
    // the function identity never changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  /**
   * THE PRINTED PAGE IS NAMED AFTER THE PAGE — not "React App".
   *
   * Chrome puts `document.title` in the printed page header and offers it as the PDF
   * filename. Records got their own name in 43c9882 and eleven other surfaces followed,
   * but a plain Ctrl+P on a LIST went through none of that machinery: a printed contacts
   * list came out headed "React App" and saved as `React App.pdf`.
   *
   * ONE PLACE, EVERY ROUTE. This is the shell every page renders inside, so hanging it
   * here covers all ~97 pages at once, with nothing to wire per page and no route→name
   * map to fall out of date. The name is the page's own <h1> (lib/printDocumentTitle),
   * so the tab and the heading on the paper are the same text by construction.
   *
   * IT IS THE WEAKEST CLAIM ON THE TITLE, and both guards below are load-bearing:
   *   • a PRINTABLE DRAWER open — it prints the record, and must be titled the record.
   *     Read from the DOM (`[data-printable]`) rather than from `body.printing-drawer`,
   *     because the drawer adds that class inside its OWN beforeprint handler and this
   *     listener is registered first — the class is reliably absent when we look, so a
   *     class check would silently title every printed record "Deal Pipeline".
   *   • a DOCUMENT PRINT running — invoice or org chart, which replace the page wholesale
   *     and already name themselves.
   * The two button-driven whole-page prints (custom + weekly reports) need no guard:
   * `printThisPage` applies its name BEFORE calling window.print(), and applyPrintTitle
   * refuses to overwrite a swap already in flight.
   *
   * NO SECOND MECHANISM: same `applyPrintTitle` / `restorePrintTitle` pair, same single
   * saved value, so a drawer print and a page print can never both think they own the tab.
   * Restored on afterprint — which fires on CANCEL as well as completion — and again on
   * unmount, so a rename can never outlive the print.
   */
  useEffect(() => {
    const DOCUMENT_PRINTS = ['printing-invoice', 'printing-orgchart'];
    const before = () => {
      if (document.querySelector('.p-drawer-panel[data-printable]')) return;
      if (DOCUMENT_PRINTS.some(c => document.body.classList.contains(c))) return;
      applyPrintTitle(printedPageTitle());
    };
    const after = () => restorePrintTitle();
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => {
      window.removeEventListener('beforeprint', before);
      window.removeEventListener('afterprint', after);
      restorePrintTitle();
    };
  }, []);

  /** Called by the Topbar hamburger button. Stable reference for React.memo(Topbar). */
  const handleToggleSidebar = useCallback(() => {
    if (isMobile) {
      setMobileOpen(o => !o);
    } else {
      setCollapsed(c => !c);
    }
  }, [isMobile]);

  /** Stable close callback passed to Sidebar. Avoids unnecessary re-renders. */
  const handleMobileClose = useCallback(() => setMobileOpen(false), []);

  /**
   * On desktop: content is pushed right by the sidebar width.
   * On mobile:  content always starts at the left edge (overlay doesn't push).
   */
  /**
   * ⛔ SOME ROUTES BRING THEIR OWN LEFT RAIL, AND TWO RAILS IS WORSE THAN ONE EMPTY ONE.
   *
   * `/mail` renders the mail rail — a Compose slot, its folders and a sync footer — which
   * a module's section list cannot express. Without this the app's own 256px rail still
   * renders beside it, EMPTY, and the page starts 256px further right. That empty rail is
   * exactly what Lis reported on 2026-09-08.
   *
   * See `lib/ownRailRoutes.ts` for why the rule is "supplies its own rail" and NOT the
   * broader "hide when the rail is empty": the reach of the broader rule was measured
   * twice, wrongly, and shipping a rule whose blast radius is unknown changes screens
   * nobody looked at.
   *
   * ⚠️ THREE THINGS CONSUME THE RAIL'S WIDTH AND ALL THREE MUST AGREE, or the rail is
   * hidden while the page still sits in a 256px hole: the <aside> below, this margin, and
   * AnnouncementBanner's left offset. They are kept together here deliberately.
   */
  const location = useLocation();
  const ownRail = routeSuppliesOwnRail(location.pathname);

  const contentMargin = isMobile || ownRail
    ? 'ml-0'
    : collapsed ? 'ml-16' : 'ml-64';

  return (
    /* `app-shell` is load-bearing for printing, not styling. index.css's print
       default hides every child of this element except <main>, so the class is
       what tells the stylesheet where the chrome/content boundary is. Renaming or
       removing it makes every page print the sidebar and topbar again.

       The wrapper is also the wash's home (position:relative + isolation:
       isolate create the stacking context that lets the z-index:-1 wash paint
       BEHIND the whole shell). The wash lives here, at the shell level, so it
       spans the viewport like the fixed sidebar/topbar — a wash sized to the
       canvas content column (its old home) ended at x≈1780 on wide windows,
       leaving a flat --p-canvas band to its right. Neither the sidebar nor the
       topbar can turn this wrapper into a containing-block trap: no transform,
       no filter here. */
    <div
      className="app-shell min-h-screen"
      style={{ position: 'relative', isolation: 'isolate' }}
    >
      {/* Colour wash BEHIND the shell's content plane — the frosted cards
          sample this. Inline style (no Tailwind gradient utilities). */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: -1,
          pointerEvents: 'none',
          backgroundImage: 'var(--p-wash)',
        }}
      />

      {/* ── Mobile backdrop ──────────────────────────────────────────────── */}
      {isMobile && mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm"
          onClick={handleMobileClose}
          aria-hidden="true"
        />
      )}

      {/* ── Context sidebar ──────────────────────────────────────────────── */}
      {/* Not rendered at all on an own-rail route — hiding it with CSS would leave it in
          the tab order and in the print tree, and the print stylesheet keys off
          `.app-shell > *:not(main)`. */}
      {!ownRail && (
      <Sidebar
        collapsed={isMobile ? false : collapsed}
        mobileOpen={mobileOpen}
        isMobile={isMobile}
        onMobileClose={handleMobileClose}
        /* ⛔ THE SAME CALLBACK THE TOP BAR'S HAMBURGER USES, NOT A SECOND ONE.
           The approved design puts the collapse control inside the sidebar's own
           "Apps" header. Giving it its own state would be two sources of truth for
           one 256<->64 width, free to disagree; this is the identical function, so
           either control drives the other's icon. The hamburger is deliberately
           LEFT IN PLACE — it is the only way to open the sidebar on mobile, where
           the Apps header is not on screen. */
        onToggleCollapsed={handleToggleSidebar}
      />
      )}

      {/* ── Top bar ──────────────────────────────────────────────────────── */}
      <Topbar
        onToggleSidebar={handleToggleSidebar}
        collapsed={isMobile ? false : collapsed}
        isMobile={isMobile}
      />

      {/* ── Announcement banner ───────────────────────────────────────────── */}
      {/* Same sidebar-aware offset the Topbar gets, so the banner clears the
          fixed sidebar instead of hiding its first ~256px behind it. */}
      <AnnouncementBanner collapsed={isMobile ? false : collapsed} isMobile={isMobile || ownRail} />

      {/* ── Main content ─────────────────────────────────────────────────── */}
      {/*
        * <main> is wrapped in <ErrorBoundary> so any render error in a
        * page component shows a friendly recovery card instead of
        * blanking the whole app (Roadmap Session 30 — Performance &
        * Testing). The boundary lives INSIDE the layout so the sidebar
        * and topbar remain interactive even when the current route
        * crashed — the user can navigate away without reloading.
        */}
      <main
        className={`pt-16 min-h-screen transition-all duration-300 ${contentMargin}`}
      >
        {/* Trial banner (screen 9a) — above the page content, like the
            announcement banner but scoped to the content column. */}
        <TrialBanner />
        <div className="p-3 sm:p-4 lg:p-6">
          {/* Breadcrumbs REMOVED app-wide (Lis's UI-fixes review, 13 Aug) — every page
              now starts directly at its own title, the way the Deals page always did. */}
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>

      {/* ── Floating overlays ────────────────────────────────────────────── */}
      {/* Quick-create actions live in "+ New" / ⌘K (the command palette); the
          former bottom-left "+" FAB was removed as a duplicate. */}

      {/* Real-time notification toasts — bottom-right stack */}
      <ToastContainer />

      <CommandPalette />

      {/* PulseBot — dockable assistant panel. Opened from the combined Help &
          Assistant hub (below) or ⌘J; no separate floating button. */}
      <PulseBotPanel />

      {/* Single bottom-right hub: assistant (PulseBot) + help/knowledge base. */}
      <HelpWidget
        onShowShortcuts={() => setShortcutsOpen(true)}
        onReplayTour={() => {
          localStorage.removeItem('pulsehq_tour_seen');
          setTourRun(true);
        }}
      />

      <KeyboardShortcutsModal
        open={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
      />

      <OnboardingTour
        run={tourRun}
        onComplete={() => setTourRun(false)}
      />

      {/* GDPR cookie consent banner — shown once per version until dismissed */}
      <CookieConsentBanner />
    </div>
  );
}
